import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaClock } from "react-icons/fa";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({
        full_name: "",
        company_name: "",
        email: "",
        phone: "",
        service: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to submit form. Please try again or contact us directly.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-2xl text-blue-500" />,
      label: "Registered Office Address",
      value: "9th Main, 7th Sector, HSR Layout, Bangalore, Bangalore South, Karnataka, India – 560102"
    },
    {
      icon: <FaEnvelope className="text-2xl text-blue-500" />,
      label: "Email",
      value: "salesops@devopsmitra.com"
    },
    {
      icon: <FaPhone className="text-2xl text-blue-500" />,
      label: "Phone",
      value: "+91 8296425797"
    },
    {
      icon: <FaClock className="text-2xl text-blue-500" />,
      label: "Business Hours",
      value: "Monday – Saturday, 9:00 AM – 7:00 PM"
    }
  ];

  return (
    <div className="pt-20">
      {/* Header Section */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-lg sm:text-xl text-slate-300">
              Have a question or ready to get started? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-8 text-slate-900">Contact Details</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                    <div className="mt-1">{info.icon}</div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">{info.label}</p>
                      <p className="text-slate-600">{info.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <img 
                  src="https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&q=80&w=800" 
                  alt="Bangalore skyline"
                  className="rounded-xl shadow-lg"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-slate-50 p-8 rounded-xl border border-slate-200">
                <h2 className="text-3xl font-bold mb-6 text-slate-900">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      data-testid="full-name-input"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      data-testid="company-name-input"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service">Service Interested In *</Label>
                    <Select onValueChange={handleServiceChange} value={formData.service} required>
                      <SelectTrigger className="mt-2" data-testid="service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IT Consulting">IT Consulting</SelectItem>
                        <SelectItem value="Cloud Solutions">Cloud Solutions</SelectItem>
                        <SelectItem value="Procurement">Procurement</SelectItem>
                        <SelectItem value="Networking">Networking</SelectItem>
                        <SelectItem value="Cybersecurity">Cybersecurity</SelectItem>
                        <SelectItem value="DevOps">DevOps</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message">Message / Tell us about your requirement *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="message-textarea"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="mt-2"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="submit-contact-form"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
                  >
                    {isSubmitting ? "Sending..." : "Send My Enquiry →"}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
